import { createSVGElement } from "../../util/dom-util.js";
import { TileLayer } from "./tile-layer.js";
const debugEther = true;
const debugCoordinate = true;
/**
 * Extend this class and override the essential methods in order to
 * draw amazing SVG stuff on a map. The default (i.e. this implementation)
 * version will display the longitudes and latitudes plus the tile boundaries
 * First construct an instance of this class (or its derivatives) and call
 * addTo(map: L.Map) to make it visible on the given map. Call remove()
 * to remove it again.
 */
export class SVGTileLayer extends TileLayer {
    debug;
    viewBoxes = new Map();
    constructor(layer, debug = false) {
        super(layer);
        this.debug = debug;
    }
    removeTile(key) {
        super.removeTile(key);
        this.viewBoxes.delete(key);
    }
    /**
     * Override this method. The default has an origin at 0,0 and a size equal to the given tileSize
     * @param coords the tile coordinates
     * @param tileSize the tile size
     * @param bounds the geodetic tile bounds
     * @returns the view box to use for the svg on this tile
     */
    getViewBox(coords, tileSize, bounds) {
        return {
            minX: 0,
            minY: 0,
            width: tileSize.x,
            height: tileSize.y,
        };
    }
    /**
     * Override this method. Calling this method implementation will display coordinates
     * and tile boundaries when debug is enabled. So you must override this method and NOT
     * call this implementation if you do not want to display these debugging features while the
     * debug flag has ben set during construction.
     * @param elem The HTMLDivElement | SVGSVGElement for this tile
     * @param coords the tile coordinates
     * @param properties the SVGTileProperties
     * @returns undefined when all went well, otherwise an Error object (Promise<Error | undefined>).
     */
    async initTileContent(elem, coords, properties) {
        let error = undefined;
        if (this.debug) {
            debugTile(elem, properties.bounds, properties.viewBox);
        }
        return error;
    }
    createTileElement(key, coords, tileSize, bounds, ownerDocument) {
        const elem = createSVGElement("svg", ownerDocument);
        const viewBox = this.getViewBox(coords, tileSize, bounds);
        this.viewBoxes.set(key, viewBox);
        elem.setAttribute("viewBox", `${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`);
        return elem;
    }
    /**
     * Constructs  the tile properties for given key
     * @param key the tile key
     * @returns the (SVG)TileProperties
     */
    getTileProperties(key) {
        const tileProperties = super.getTileProperties(key);
        const viewBox = this.viewBoxes.get(key);
        return Object.assign(tileProperties, { viewBox });
    }
}
const debugCoordinateTextSize = 10;
const addGeodeticCoordinate = (coord, x, y, color, elem, horizontal = true) => {
    let text = createSVGElement("text", document);
    text.setAttribute("x", x.toString());
    text.setAttribute("y", y.toString());
    text.setAttribute("fill", color);
    if (!horizontal) {
        text.setAttribute("transform", `rotate(90 ${x} ${y})`);
    }
    text.innerHTML = coord.toFixed(4);
    elem.appendChild(text);
    return text;
};
const addDebugVisuals = (elem, length, color, strokeWidth, viewBox) => {
    let vLine = createSVGElement("line", document);
    vLine.setAttribute("stroke", color);
    vLine.setAttribute("stroke-width", strokeWidth.toString());
    let hLine = createSVGElement("line", document);
    hLine.setAttribute("stroke", color);
    hLine.setAttribute("stroke-width", strokeWidth.toString());
    vLine.setAttribute("x1", viewBox.minX.toString());
    vLine.setAttribute("y1", (-length / 2).toString());
    vLine.setAttribute("x2", viewBox.minX.toString());
    vLine.setAttribute("y2", (length / 2).toString());
    hLine.setAttribute("y1", viewBox.minY.toString());
    hLine.setAttribute("x1", (-length / 2).toString());
    hLine.setAttribute("y2", viewBox.minY.toString());
    hLine.setAttribute("x2", (length / 2).toString());
    elem.appendChild(vLine);
    elem.appendChild(hLine);
    return elem;
};
const debugTile = (elem, bounds, viewBox) => {
    if (debugEther) {
        addDebugVisuals(elem, viewBox.width / 10, "#804000", 0.25, viewBox);
    }
    if (debugCoordinate) {
        addGeodeticCoordinate(bounds.northEast.lat, viewBox.width / 20 + (3 * viewBox.width) / 100 + viewBox.minX, debugCoordinateTextSize / 2 + viewBox.minY, "#0000C0", elem);
        addGeodeticCoordinate(bounds.southWest.lng, -debugCoordinateTextSize / 2 + viewBox.minX, viewBox.width / 20 + (3 * viewBox.width) / 100 + viewBox.minY, "#C000C0", elem, false);
    }
    return elem;
};
